// frontend-participant/src/components/BenchmarkView.js
import React, { useState, useEffect } from 'react';
import './BenchmarkView.css';

const BenchmarkView = ({ socket, room }) => {
  const [results, setResults] = useState(null);
  const [selectedMismatch, setSelectedMismatch] = useState(null);
  const [filterType, setFilterType] = useState('all'); // all, tool_name, args_only, missing, unexpected
  const [filterAgentType, setFilterAgentType] = useState('all');
  const [comparisonMode, setComparisonMode] = useState('strict'); // strict, tool_only
  
  // Test controls
  const [availableJourneys, setAvailableJourneys] = useState([]);
  const [selectedJourney, setSelectedJourney] = useState('');
  const [runAllJourneys, setRunAllJourneys] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  
  // For drilling into a specific test from AllTestsView
  const [selectedTestForDetail, setSelectedTestForDetail] = useState(null);

  const API_ORIGIN = process.env.NODE_ENV === 'development' 
    ? "http://localhost:5000" 
    : "";

  // Fetch available journeys on mount
  useEffect(() => {
    if (!room) return;
    
    fetch(`${API_ORIGIN}/api/benchmark/${room}/journeys`)
      .then(r => r.json())
      .then(data => {
        if (data.status === 'success') {
          setAvailableJourneys(data.journeys || []);
          if (data.journeys && data.journeys.length > 0) {
            setSelectedJourney(data.journeys[0]);
          }
        }
      })
      .catch(err => console.error('Failed to fetch journeys:', err));
  }, [room, API_ORIGIN]);

  useEffect(() => {
    if (!socket) return;

    // Listen for benchmark results
    const handleBenchmarkResult = (data) => {
      console.log('Received benchmark result:', data);
      setResults(data);
      setIsRunning(false);
    };

    socket.on('benchmark_result', handleBenchmarkResult);

    return () => {
      socket.off('benchmark_result', handleBenchmarkResult);
    };
  }, [socket]);

  const handleRunTest = async () => {
    if (isRunning) return;
    
    setIsRunning(true);
    setResults(null); // Clear previous results
    
    try {
      const requestBody = runAllJourneys ? {} : { thread_name: selectedJourney };
      
      const response = await fetch(`${API_ORIGIN}/api/benchmark/${room}/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      
      if (data.status === 'error') {
        alert(`Failed to run benchmark: ${data.message}`);
        setIsRunning(false);
      }
      // Results will be received via socket event
    } catch (error) {
      console.error('Error running benchmark:', error);
      alert('Failed to run benchmark: Network error');
      setIsRunning(false);
    }
  };

  if (!results && !isRunning) {
    return (
      <div className="benchmark-view">
        <div className="benchmark-waiting">
          <div className="benchmark-icon">🧪</div>
          <h2>Benchmark Testing</h2>
          <p>Run tests to analyze AI agent performance on saved user journeys</p>
          
          <div className="test-controls">
            <div className="control-group">
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={runAllJourneys}
                  onChange={(e) => setRunAllJourneys(e.target.checked)}
                />
                <span>Run all journeys ({availableJourneys.length})</span>
              </label>
            </div>
            
            {!runAllJourneys && (
              <div className="control-group">
                <label>Select Journey:</label>
                <select 
                  value={selectedJourney} 
                  onChange={(e) => setSelectedJourney(e.target.value)}
                  disabled={availableJourneys.length === 0}
                >
                  {availableJourneys.length === 0 ? (
                    <option value="">Loading journeys...</option>
                  ) : (
                    availableJourneys.map(journey => (
                      <option key={journey} value={journey}>{journey}</option>
                    ))
                  )}
                </select>
              </div>
            )}
            
            <button
              className="run-test-button"
              onClick={handleRunTest}
              disabled={!runAllJourneys && !selectedJourney}
            >
              {runAllJourneys ? '▶ Run All Tests' : '▶ Run Test'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  
  if (isRunning) {
    return (
      <div className="benchmark-view">
        <div className="benchmark-waiting">
          <div className="benchmark-icon">⏳</div>
          <h2>Running Tests...</h2>
          <p>Analyzing {runAllJourneys ? 'all journeys' : selectedJourney}</p>
          <div className="spinner-large"></div>
        </div>
      </div>
    );
  }

  if (results.status === 'error') {
    return (
      <div className="benchmark-view">
        <div className="benchmark-error">
          <h2>❌ Error</h2>
          <p>{results.message}</p>
        </div>
      </div>
    );
  }

  // Handle both single and all-journeys results
  const isSingleTest = results.test_type === 'single';
  const testResult = isSingleTest ? results.result : null;
  const allResults = isSingleTest ? null : results.results;

  // If user selected a specific test from AllTestsView, show its detail
  if (selectedTestForDetail && allResults && allResults[selectedTestForDetail]) {
    return <SingleTestView 
      result={allResults[selectedTestForDetail]}
      comparisonMode={comparisonMode}
      setComparisonMode={setComparisonMode}
      onBack={() => setSelectedTestForDetail(null)}
    />;
  }

  // For single test, show detailed view
  if (isSingleTest && testResult) {
    return <SingleTestView 
      result={testResult} 
      filterType={filterType}
      setFilterType={setFilterType}
      filterAgentType={filterAgentType}
      setFilterAgentType={setFilterAgentType}
      comparisonMode={comparisonMode}
      setComparisonMode={setComparisonMode}
      selectedMismatch={selectedMismatch}
      setSelectedMismatch={setSelectedMismatch}
    />;
  }

  // For all tests, show summary view
  if (allResults) {
    return <AllTestsView 
      results={allResults} 
      onSelectTest={setSelectedTestForDetail}
    />;
  }

  return null;
};

// Component to display a single test result
const SingleTestView = ({ 
  result, 
  filterType, 
  setFilterType,
  filterAgentType,
  setFilterAgentType,
  comparisonMode,
  setComparisonMode,
  selectedMismatch,
  setSelectedMismatch,
  onBack  // Optional: callback to return to AllTestsView
}) => {
  const [expandedSection, setExpandedSection] = useState(null); // 'type', 'agent', 'turn', or null
  const [expandedItem, setExpandedItem] = useState(null); // which specific item is expanded
  
  // Get metrics for both modes
  const strictMetrics = result.metrics.strict;
  const toolOnlyMetrics = result.metrics.tool_only;
  
  // Filter mismatches based on comparison mode
  const getFilteredMismatches = (mode) => {
    let filtered = result.mismatches || [];
    if (mode === 'tool_only') {
      filtered = filtered.filter(m => m.mismatch_type !== 'args_only');
    }
    return filtered;
  };
  
  const strictMismatches = getFilteredMismatches('strict');
  const toolOnlyMismatches = getFilteredMismatches('tool_only');
  
  // Get mismatches for current mode
  const currentMismatches = comparisonMode === 'strict' ? strictMismatches : toolOnlyMismatches;

  // Get unique agent types
  const agentTypes = [...new Set((result.mismatches || []).map(m => m.agent_type))];
  
  // Group mismatches by category for drill-down
  const mismatchesByType = {};
  const mismatchesByAgent = {};
  const mismatchesByTurn = {};
  
  currentMismatches.forEach(m => {
    // By type
    if (!mismatchesByType[m.mismatch_type]) mismatchesByType[m.mismatch_type] = [];
    mismatchesByType[m.mismatch_type].push(m);
    
    // By agent type
    if (!mismatchesByAgent[m.agent_type]) mismatchesByAgent[m.agent_type] = [];
    mismatchesByAgent[m.agent_type].push(m);
    
    // By turn
    if (!mismatchesByTurn[m.turn_number]) mismatchesByTurn[m.turn_number] = [];
    mismatchesByTurn[m.turn_number].push(m);
  });

  return (
    <div className="benchmark-view">
      {/* Header */}
      <div className="benchmark-header">
        {onBack && (
          <button className="back-button" onClick={onBack}>
            ← Back to All Tests
          </button>
        )}
        <h2>🧪 Benchmark Results</h2>
        <div className="benchmark-thread-id">{result.thread_id}</div>
      </div>

      {/* Metrics Overview */}
      <div className="metrics-overview">
        <div className="metric-row">
          <div className="metric-card neutral">
            <div className="metric-label">Total Turns</div>
            <div className="metric-value">{result.total_turns}</div>
          </div>
          <div className="metric-card neutral">
            <div className="metric-label">Tool Calls</div>
            <div className="metric-value">{result.total_tool_calls}</div>
          </div>
        </div>
        
        {/* Side-by-side comparison */}
        <div className="comparison-section">
          <div className="comparison-mode-header">
            <h3>Comparison Modes</h3>
            <p>Click to switch between strict and tool-only analysis</p>
          </div>
          
          <div className="comparison-cards">
            {/* Strict Mode Card */}
            <div 
              className={`comparison-card ${comparisonMode === 'strict' ? 'active' : ''}`}
              onClick={() => setComparisonMode('strict')}
            >
              <div className="comparison-card-header">
                <div className="mode-badge strict">Strict Mode</div>
                <div className="mode-description">Tool name + arguments must match</div>
              </div>
              <div className="comparison-stats">
                <div className="stat-item">
                  <div className="stat-label">Success Rate</div>
                  <div className="stat-value success">{(strictMetrics.success_rate * 100).toFixed(1)}%</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Errors</div>
                  <div className="stat-value error">{strictMetrics.total_errors}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Correct</div>
                  <div className="stat-value">{result.total_tool_calls - strictMetrics.total_errors}</div>
                </div>
              </div>
            </div>
            
            {/* Tool-Only Mode Card */}
            <div 
              className={`comparison-card ${comparisonMode === 'tool_only' ? 'active' : ''}`}
              onClick={() => setComparisonMode('tool_only')}
            >
              <div className="comparison-card-header">
                <div className="mode-badge tool-only">Tool-Only Mode</div>
                <div className="mode-description">Only tool name must match</div>
              </div>
              <div className="comparison-stats">
                <div className="stat-item">
                  <div className="stat-label">Success Rate</div>
                  <div className="stat-value success">{(toolOnlyMetrics.success_rate * 100).toFixed(1)}%</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Errors</div>
                  <div className="stat-value error">{toolOnlyMetrics.total_errors}</div>
                </div>
                <div className="stat-item">
                  <div className="stat-label">Correct</div>
                  <div className="stat-value">{result.total_tool_calls - toolOnlyMetrics.total_errors}</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Drill-Down Analysis */}
      <div className="drill-down-section">
        <h3>📊 Detailed Analysis - {comparisonMode === 'strict' ? 'Strict Mode' : 'Tool-Only Mode'}</h3>
        
        {currentMismatches.length === 0 ? (
          <div className="no-mismatches">
            🎉 Perfect score! No mismatches in {comparisonMode} mode.
          </div>
        ) : (
          <div className="drill-down-tabs">
            {/* Tab Headers */}
            <div className="tab-headers">
              <button 
                className={`tab-header ${expandedSection === 'type' ? 'active' : ''}`}
                onClick={() => setExpandedSection(expandedSection === 'type' ? null : 'type')}
              >
                By Mismatch Type ({Object.keys(mismatchesByType).length})
              </button>
              <button 
                className={`tab-header ${expandedSection === 'agent' ? 'active' : ''}`}
                onClick={() => setExpandedSection(expandedSection === 'agent' ? null : 'agent')}
              >
                By Agent Type ({Object.keys(mismatchesByAgent).length})
              </button>
              <button 
                className={`tab-header ${expandedSection === 'turn' ? 'active' : ''}`}
                onClick={() => setExpandedSection(expandedSection === 'turn' ? null : 'turn')}
              >
                By Turn ({Object.keys(mismatchesByTurn).length})
              </button>
            </div>
            
            {/* Tab Content */}
            {expandedSection === 'type' && (
              <DrillDownContent 
                groupedData={mismatchesByType}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                selectedMismatch={selectedMismatch}
                setSelectedMismatch={setSelectedMismatch}
                labelPrefix="mismatch_type"
              />
            )}
            
            {expandedSection === 'agent' && (
              <DrillDownContent 
                groupedData={mismatchesByAgent}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                selectedMismatch={selectedMismatch}
                setSelectedMismatch={setSelectedMismatch}
                labelPrefix="agent_type"
              />
            )}
            
            {expandedSection === 'turn' && (
              <DrillDownContent 
                groupedData={mismatchesByTurn}
                expandedItem={expandedItem}
                setExpandedItem={setExpandedItem}
                selectedMismatch={selectedMismatch}
                setSelectedMismatch={setSelectedMismatch}
                labelPrefix="turn"
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

// Drill-down content component for hierarchical navigation
const DrillDownContent = ({ 
  groupedData, 
  expandedItem, 
  setExpandedItem,
  selectedMismatch,
  setSelectedMismatch,
  labelPrefix 
}) => {
  return (
    <div className="drill-down-content">
      {Object.entries(groupedData).map(([key, mismatches]) => (
        <div key={key} className="drill-down-group">
          {/* Category Header */}
          <div 
            className={`category-header ${expandedItem === key ? 'expanded' : ''}`}
            onClick={() => setExpandedItem(expandedItem === key ? null : key)}
          >
            <div className="category-info">
              <span className={`category-badge ${key}`}>{key}</span>
              <span className="category-count">{mismatches.length} error{mismatches.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="expand-icon">{expandedItem === key ? '▼' : '▶'}</div>
          </div>
          
          {/* Expanded: Show list of q_ids */}
          {expandedItem === key && (
            <div className="category-items">
              {mismatches.map((mismatch, index) => (
                <div 
                  key={index}
                  className={`mismatch-card ${selectedMismatch === mismatch ? 'selected' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedMismatch(selectedMismatch === mismatch ? null : mismatch);
                  }}
                >
                  {/* Compact View */}
                  <div className="mismatch-card-header">
                    <div className="mismatch-card-left">
                      <span className="q-id-badge">{mismatch.q_id}</span>
                      <span className="agent-type-badge">{mismatch.agent_type}</span>
                    </div>
                    <div className="mismatch-card-right">
                      <span className="location-badge">
                        Turn {mismatch.turn_number}
                        {mismatch.wave_number !== undefined && ` • Wave ${mismatch.wave_number}`}
                      </span>
                      <span className="expand-detail-icon">
                        {selectedMismatch === mismatch ? '▲' : '▼'}
                      </span>
                    </div>
                  </div>
                  
                  {/* Expanded: Show full details */}
                  {selectedMismatch === mismatch && (
                    <div className="mismatch-card-details">
                      <div className="detail-grid">
                        <div className="detail-column">
                          <h4>✓ Expected</h4>
                          <div className="tool-info">
                            <div className="tool-name-display">{mismatch.expected_tool || 'N/A'}</div>
                            <pre className="tool-args-display">{JSON.stringify(mismatch.expected_args, null, 2)}</pre>
                          </div>
                        </div>
                        
                        <div className="detail-column">
                          <h4>✗ Actual</h4>
                          <div className="tool-info">
                            <div className="tool-name-display">{mismatch.actual_tool || 'N/A'}</div>
                            <pre className="tool-args-display">{JSON.stringify(mismatch.actual_args, null, 2)}</pre>
                          </div>
                        </div>
                      </div>
                      
                      {mismatch.is_tool_correct && (
                        <div className="info-badge success">
                          ✓ Tool name is correct (only arguments differ)
                        </div>
                      )}
                      
                      <div className="prompt-section">
                        <div className="prompt-header">
                          <h4>🤖 System Prompt</h4>
                          <button 
                            className="copy-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(mismatch.prompt);
                              alert('System prompt copied to clipboard!');
                            }}
                          >
                            📋 Copy
                          </button>
                        </div>
                        <pre className="agent-prompt-display">{mismatch.prompt}</pre>
                      </div>
                      
                      {/* Message History */}
                      {mismatch.message_history && mismatch.message_history.length > 0 && (
                        <div className="message-history-section">
                          <div className="prompt-header">
                            <h4>💬 Conversation History ({mismatch.message_history.length} messages)</h4>
                            <button 
                              className="copy-button"
                              onClick={(e) => {
                                e.stopPropagation();
                                const historyText = mismatch.message_history
                                  .map(msg => `[${msg.role.toUpperCase()}]: ${msg.content}`)
                                  .join('\n\n');
                                navigator.clipboard.writeText(historyText);
                                alert('Message history copied to clipboard!');
                              }}
                            >
                              📋 Copy All
                            </button>
                          </div>
                          <div className="message-history-list">
                            {mismatch.message_history.map((msg, idx) => (
                              <div key={idx} className={`message-item ${msg.role}`}>
                                <div className="message-role-badge">{msg.role}</div>
                                <div className="message-content">{msg.content}</div>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

// Component to display all tests summary
const AllTestsView = ({ results, onSelectTest }) => {
  const [expandedTest, setExpandedTest] = useState(null);

  const testNames = Object.keys(results);
  
  return (
    <div className="benchmark-view">
      <div className="benchmark-header">
        <h2>🧪 All Tests Summary</h2>
        <div className="benchmark-thread-id">{testNames.length} journeys tested</div>
      </div>

      <div className="all-tests-grid">
        {testNames.map(testName => {
          const result = results[testName];
          const strictMetrics = result.metrics.strict;
          const toolOnlyMetrics = result.metrics.tool_only;
          
          return (
            <div 
              key={testName} 
              className="test-summary-card"
            >
              <div className="test-card-header">
                <h3>{testName}</h3>
                <button 
                  className="view-details-button"
                  onClick={() => onSelectTest(testName)}
                >
                  🔍 View Details
                </button>
              </div>
              
              <div className="summary-metrics">
                <div className="summary-metric">
                  <span className="summary-label">Turns:</span>
                  <span className="summary-value">{result.total_turns}</span>
                </div>
                <div className="summary-metric">
                  <span className="summary-label">Tool Calls:</span>
                  <span className="summary-value">{result.total_tool_calls}</span>
                </div>
              </div>

              <div className="summary-rates">
                <div className="rate-item">
                  <div className="rate-label">Strict</div>
                  <div className="rate-bar">
                    <div 
                      className="rate-fill strict" 
                      style={{ width: `${strictMetrics.success_rate * 100}%` }}
                    />
                  </div>
                  <div className="rate-value">{(strictMetrics.success_rate * 100).toFixed(1)}%</div>
                </div>
                
                <div className="rate-item">
                  <div className="rate-label">Tool Only</div>
                  <div className="rate-bar">
                    <div 
                      className="rate-fill tool-only" 
                      style={{ width: `${toolOnlyMetrics.success_rate * 100}%` }}
                    />
                  </div>
                  <div className="rate-value">{(toolOnlyMetrics.success_rate * 100).toFixed(1)}%</div>
                </div>
              </div>

              <div className="expand-toggle-section">
                <button 
                  className="expand-toggle-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setExpandedTest(expandedTest === testName ? null : testName);
                  }}
                >
                  {expandedTest === testName ? '▼ Hide Summary' : '▶ Show Summary'}
                </button>
              </div>

              {expandedTest === testName && (
                <div className="test-detail">
                  <h4>Error Breakdown</h4>
                  <div className="error-types">
                    {Object.entries(result.mismatches_by_type || {}).map(([type, count]) => (
                      <div key={type} className="error-type-item">
                        <span className={`error-type-badge ${type}`}>{type}</span>
                        <span className="error-count">{count}</span>
                      </div>
                    ))}
                  </div>
                  
                  <h4>Agent Breakdown</h4>
                  <div className="agent-types">
                    {Object.entries(result.mismatches_by_agent_type || {}).map(([type, count]) => (
                      <div key={type} className="agent-type-item">
                        <span className="agent-type-name">{type}</span>
                        <span className="agent-count">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default BenchmarkView;
